<script setup>
import { computed, ref, reactive, onMounted, onBeforeUnmount, resolveComponent, toRaw } from 'vue'
import { blocks } from "@/constants/dynamicBlocks.json"
import { useIterableLiveBlocksMeta } from '~/composables/useIterableLiveBlocksMeta'
import {
  findMatchingLiveIterableBlock,
  mergeIterableDynamicZoneBlock,
  numericPlacementIdFromIterableRelation,
  unwrapStrapiScalar
} from '~/utils/iterable-live-merge'
import { useIterable } from '~/composables/useIterable'

const CRITICAL_BLOCKS = new Set([
  'GenericContent',
  'MediaHTML',
  'CardsTopic'
]);

// Map is eager here; `Map.vue` defers loading the Google Maps API near the viewport.
const DEFERRED_DYNAMIC_BLOCKS = new Set([]);

const SPACERS = {
  0: '0px',
  1: '10px',
  2: '20px',
  3: '30px',
  4: '40px',
  5: '50px',
  6: '60px',
  7: '70px',
  8: '80px',
  9: '90px',
};

const props = defineProps({
  sections: Object,
  id: String,
  location: Object,
  pulseData: Object,
  gridData: Object,
  forceMobileMode: Boolean
});

/** Same ref as `useAsyncData` default — matches `CardsProgram.vue`; composable runs unconditionally in setup (`server: false` in composable). */
const { data: iterableLiveById } = useIterableLiveBlocksMeta()

const {
  isEnabled: iterableSdkReady,
  identityReady: iterableIdentityReady,
  hasIterableIdentity: iterableHasIdentity,
  embeddedMessagesByPlacement
} = useIterable()

/**
 * Live Strapi merge for Iterable rows runs only after client mount (`mergeIterableElement` checks this).
 * Keeps SSR/hydration output on cached `sections` until `onMounted`, then applies live-meta without mismatch.
 */
const isHydrated = ref(false)

const liveIterableBlocks = computed(() => iterableLiveById.value?.blocks ?? [])
const ITERABLE_BLOCK = 'dynamic-blocks.iterable'

const refs = ref([])
const nuxtApp = useNuxtApp()
const registeredComponents = nuxtApp.vueApp?._context?.components || {}
const sectionRefs = ref(new Map())
const visibleSections = reactive({})
let sectionObserver = null

/** Strapi v4/v5: `id` may be number, string, or `{ documentId }` — never interpolate raw objects in keys/classes. */
function blockIdKeySegment (data) {
  const idValue = data?.id
  if (idValue == null) return 'section'
  if (typeof idValue === 'string' || typeof idValue === 'number') return String(idValue)
  if (typeof idValue === 'object' && idValue !== null && 'documentId' in idValue) {
    const documentId = idValue.documentId
    if (typeof documentId === 'string' || typeof documentId === 'number') return String(documentId)
  }
  return 'section'
}

function safeBackgroundColorClass (strapiColorValue) {
  const scalar = unwrapStrapiScalar(strapiColorValue)
  if (scalar == null || scalar === '') return 'bg-white'
  if (typeof scalar === 'string') {
    const normalizedToken = scalar.toLowerCase().replace(/\s+/g, '-')
    return 'bg-' + normalizedToken
  }
  return 'bg-white'
}

/** Iterable sync can re-run `components` while Strapi merge supplies odd shapes — never bind non-string to `:class`. */
function safeSectionBackgroundClass (component) {
  const sectionBackgroundColor = component?.backgroundColor
  return typeof sectionBackgroundColor === 'string' && sectionBackgroundColor.length > 0
    ? sectionBackgroundColor
    : 'bg-white'
}

/** Padding + divider + background class from Strapi `options` (same rules as before). */
function sectionRowLayoutFromOptions (options) {
  const sectionOptions = options || {}
  const cssLengthForSpacerKey = (spacerKey) => SPACERS[spacerKey] || '0px'
  const mobilePaddingTopValue =
    sectionOptions.mobilePaddingTop !== 0
      ? cssLengthForSpacerKey(sectionOptions.mobilePaddingTop)
      : cssLengthForSpacerKey(sectionOptions.paddingTop)
  const desktopPaddingTopValue =
    sectionOptions.mobilePaddingTop !== 0 ? cssLengthForSpacerKey(sectionOptions.paddingTop) : mobilePaddingTopValue
  const mobilePaddingBottomValue =
    sectionOptions.mobilePaddingBottom !== 0
      ? cssLengthForSpacerKey(sectionOptions.mobilePaddingBottom)
      : cssLengthForSpacerKey(sectionOptions.paddingBottom)
  const desktopPaddingBottomValue =
    sectionOptions.mobilePaddingBottom !== 0 ? cssLengthForSpacerKey(sectionOptions.paddingBottom) : mobilePaddingBottomValue
  return {
    mobilePaddingTopValue,
    desktopPaddingTopValue,
    mobilePaddingBottomValue,
    desktopPaddingBottomValue,
    backgroundColor: safeBackgroundColorClass(sectionOptions.backgroundColor),
    backgroundColor: sectionOptions.backgroundColor
      ? 'bg-' + String(sectionOptions.backgroundColor).toLowerCase().replace(/\s+/g, '-')
      : 'bg-white',
    showDivider: sectionOptions.showDivider || false
  }
}

/**
 * Strict live merge: only when a unique match exists on placementId or Strapi block id.
 * Immutable shallow clone of the section row; shallow-merge options/defaults.
 */
function mergeIterableElement (element) {
  if (element == null || typeof element !== 'object') return element
  if (element?.component !== ITERABLE_BLOCK) return element
  if (!isHydrated.value) return element
  const live = findMatchingLiveIterableBlock(element, liveIterableBlocks.value)
  if (!live) return element
  return mergeIterableDynamicZoneBlock({ ...element }, live)
}

/**
 * Props passed into every dynamic-zone child (`IterableBlock`, etc.).
 * Returned as a plain object and spread onto each row so the template keeps **top-level**
 * bindings (`:refs="refs"`, `:data="component.data"`). Nesting refs inside `component.childProps`
 * breaks template ref unwrapping and can stop Iterable / other blocks from behaving correctly.
 */
function dynamicZoneChildProps (sectionSource) {
  return {
    data: sectionSource ?? {},
    location: props.location,
    refs,
    pulseData: props.pulseData,
    gridData: props.gridData,
    forceMobileMode: props.forceMobileMode
  }
}

function getBlockComponentReference (componentName, preferLazy = true) {
  if (preferLazy) {
    const lazyName = `Lazy${componentName}`
    if (registeredComponents[lazyName]) {
      return registeredComponents[lazyName]
    }
  }
  return resolveComponent(componentName)
}

function initializeSectionVisibility (renderKey, shouldDefer) {
  const hasVisibilityState = Object.prototype.hasOwnProperty.call(visibleSections, renderKey)
  if (!hasVisibilityState) {
    visibleSections[renderKey] = !shouldDefer
    return
  }

  // Never re-hide a deferred section that was already revealed.
  if (!shouldDefer) {
    visibleSections[renderKey] = true
  }
}

/**
 * Iterable CMS block: mount the dynamic-zone `<section>` only when Strapi shows the row, placement is valid,
 * SDK + identity are ready, and Iterable returned **≥1 embedded message** for that placement.
 * Aligns with `EmbeddedPlacement` / `CardsProgram` so lead-gen emails not in the Iterable audience
 * (no messages after sync) do not reserve an empty padded section.
 */
function iterableDynamicZoneSectionVisible (source) {
  if (source == null || typeof source !== 'object') {
    return false
  }

  if (source?.component !== ITERABLE_BLOCK) {
    return true
  }

  if (source?.showComponent !== true) {
    return false
  }

  const placementId = numericPlacementIdFromIterableRelation(source?.iterablePlacement)
  if (placementId == null) {
    return false
  }

  if (!import.meta.client) {
    return false
  }

  if (!iterableSdkReady.value) {
    return false
  }

  if (!iterableIdentityReady.value) {
    return false
  }

  if (!iterableHasIdentity.value) {
    return false
  }

  /**
   * `toRaw` strips Vue's reactive Proxy from the shallowRef value so the map lookup below
   * is a plain property access with no reactive-tracking side effects.
   * Without it, Vue would track `byPlacement[placementId]` as a dependency of this function,
   * causing unnecessary re-runs when unrelated reactive state changes.
   */
  const byPlacement =
    embeddedMessagesByPlacement?.value != null
      ? toRaw(embeddedMessagesByPlacement.value)
      : null
  if (byPlacement == null || typeof byPlacement !== 'object') {
    return false
  }
  const messagesForPlacement = byPlacement[placementId]
  if (!Array.isArray(messagesForPlacement) || messagesForPlacement.length === 0) {
    return false
  }

  return true
}

const components = computed(() => {
  const sections = Array.isArray(props.sections) ? props.sections : []
  return sections.map((element, index) => {
    const source =
      element?.component === ITERABLE_BLOCK ? mergeIterableElement(element) : element
    const options = source?.options || {}
    const defaults = source?.defaults || {}
    const componentName = blocks[source?.component]
    const isCritical = CRITICAL_BLOCKS.has(componentName)
    const renderKey = `${blockIdKeySegment(source)}-${source?.component ?? 'section'}-${index}`
    // Defer policy is currently frontend-controlled (Strapi `ui.options` has no `dataLoad` field).
    const shouldDefer = DEFERRED_DYNAMIC_BLOCKS.has(source?.component)

    initializeSectionVisibility(renderKey, shouldDefer)

    return {
      reference: getBlockComponentReference(componentName, !isCritical),
      ...dynamicZoneChildProps(source),
      ...sectionRowLayoutFromOptions(options),
      renderKey,
      shouldDefer,
      showComponent: source?.component === ITERABLE_BLOCK
        ? iterableDynamicZoneSectionVisible(source)
        : (defaults.showComponent || false)
    }
  })
})

const setSectionRef = (el, key, shouldDefer) => {
  if (!el) return;

  sectionRefs.value.set(key, el);
  if (!shouldDefer) {
    visibleSections[key] = true;
    return;
  }

  if (sectionObserver) {
    sectionObserver.observe(el);
  }
};

onMounted(() => {
  isHydrated.value = true

  sectionObserver = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      const target = entry.target;
      if (!(target instanceof HTMLElement)) return;
      const key = target.dataset?.deferKey;
      if (!key) return;
      visibleSections[key] = true;
      sectionObserver?.unobserve(target);
    });
  }, { rootMargin: '800px 0px' });

  sectionRefs.value.forEach((el, key) => {
    if (!visibleSections[key]) {
      sectionObserver?.observe(el);
    }
  });
});

onBeforeUnmount(() => {
  sectionObserver?.disconnect();
  sectionObserver = null;
});

const emit = defineEmits(['nestedStep', 'studentName', 'validateStep'])
const nextStep = (value) => {
 emit('nestedStep', value)
}
const studentName =(value) => {
  emit('studentName', value)
}

const stepValidation = (value) => {
  emit('validateStep', value)
}
</script>

<template>
  <div :id="id">
    <template v-for="(component, index) in components">
      <section
        :key="`${blockIdKeySegment(component?.data)}-${index}`"
        class="section-component"
        :style="{
          '--mobile-padding-top': component.mobilePaddingTopValue,
          '--desktop-padding-top': component.desktopPaddingTopValue,
          '--mobile-padding-bottom': component.mobilePaddingBottomValue,
          '--desktop-padding-bottom': component.desktopPaddingBottomValue
        }"
        :class="safeSectionBackgroundClass(component)"
        :data-defer-key="component?.renderKey"
        v-if="component?.showComponent"
        :ref="(el) => setSectionRef(el, component?.renderKey, component?.shouldDefer)"
      >
        <component
          v-if="visibleSections[component?.renderKey ?? '']"
          :is="component.reference"
          :data="component.data"
          :location="location"
          ref="refs"
          :refs="refs"
          :pulseData="pulseData"
          :gridData="gridData"
          :forceMobileMode="component.forceMobileMode"
          @nestedStep="nextStep"
          @studentName="studentName"
          @validateStep="stepValidation"
        />
        <hr class="w-100" v-if="component.showDivider">
      </section>
    </template>
  </div>
</template>

<style lang="scss">
/* Define the spacer values as CSS variables */
:root {
  --spacer-0: 0px;
  --spacer-1: 10px;
  --spacer-2: 20px;
  --spacer-3: 30px;
  --spacer-4: 40px;
  --spacer-5: 50px;
  --spacer-6: 60px;
  --spacer-7: 70px;
  --spacer-8: 80px;
  --spacer-9: 90px;
}

.section-component {
  &:has(#filter-card-product) {
    min-height: 150px;
    @include media-breakpoint-down(md) {
      min-height: 180px;
    }
  }

  &:has(#filter-card-product .heading-row) {
    min-height: 210px;
    @include media-breakpoint-down(md) {
      min-height: 240px;
    }
  }
}

/* Apply padding based on viewport size using media queries */
@include media-breakpoint-up(lg) {
  .section-component {
    padding-top: var(--desktop-padding-top);
    padding-bottom: var(--desktop-padding-bottom);
  }
}

@include media-breakpoint-down(lg) {
  .section-component {
    padding-top: var(--mobile-padding-top);
    padding-bottom: var(--mobile-padding-bottom);
  }
}
</style>
